import SixDigitCodeInput from "@via/components/code-input/six-digit-code-input.tsx";
import AuthenticatorQr from "@via/pages/authentication/mfa/authenticator-qr.tsx";
import {
  mfaErrorMessage,
  useMfaSetupConfirm,
  useMfaSetupStart,
} from "@via/pages/authentication/mfa/mfa.api.tsx";
import { LockedNotice } from "@via/pages/authentication/mfa/mfa-notices.tsx";
import { type JSX, useEffect, useState } from "react";

const PANEL = "space-y-3 rounded-md p-4 ring-1 ring-gray-200";

/**
 * Set up (no step-up) or replace (a current code, or a recovery code when the phone is lost). The
 * old authenticator keeps working until the new one is confirmed.
 */
export default function AuthenticatorSetup({
  needsStepUp,
  onDone,
  onCancel,
}: {
  needsStepUp: boolean;
  onDone: (codes: string[]) => void;
  onCancel: () => void;
}): JSX.Element {
  const start = useMfaSetupStart();
  const confirm = useMfaSetupConfirm();
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [attempt, setAttempt] = useState(0);
  const error = start.isError
    ? mfaErrorMessage(start.error)
    : confirm.isError
      ? mfaErrorMessage(confirm.error)
      : null;
  const lockedUntil = error?.code === "locked" ? error.lockedUntil : undefined;
  const bump = () => setAttempt(a => a + 1);

  // A first setup needs no step-up, so it starts on open. Once, on mount: the panel is
  // remounted each time it opens.
  useEffect(() => {
    if (!needsStepUp) start.mutate({});
  }, []);

  return (
    <div className={PANEL}>
      {!start.data && needsStepUp && (
        <>
          <h3 className="text-base font-semibold text-gray-900">
            Confirm it is you
          </h3>
          <p className="text-sm text-gray-600">
            {useRecovery
              ? "Enter one of your saved recovery codes. It is used up once accepted."
              : "Enter a code from your current authenticator app."}
          </p>
          {!useRecovery ? (
            <SixDigitCodeInput
              resetKey={attempt}
              disabled={start.isPending || lockedUntil !== undefined}
              onComplete={code => start.mutate({ code }, { onError: bump })}
            />
          ) : (
            <form
              className="space-y-2"
              onSubmit={e => {
                e.preventDefault();
                start.mutate(
                  { recoveryCode: recoveryCode.trim() },
                  { onError: bump }
                );
              }}>
              <label
                htmlFor="setup-recovery-code"
                className="block text-sm font-medium leading-6 text-gray-900">
                Recovery code
              </label>
              <input
                id="setup-recovery-code"
                autoComplete="off"
                value={recoveryCode}
                onChange={e => setRecoveryCode(e.target.value)}
                placeholder="xxxxx-xxxxx"
                className="block w-full rounded-md border-0 py-1.5 font-mono text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
              <button
                type="submit"
                disabled={
                  start.isPending ||
                  lockedUntil !== undefined ||
                  recoveryCode.trim().length < 10
                }
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50">
                Continue
              </button>
            </form>
          )}
          <button
            type="button"
            className="block text-sm font-semibold text-indigo-600 hover:text-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            // Resetting would clear the locked answer and re-enable the inputs.
            disabled={lockedUntil !== undefined}
            onClick={() => {
              start.reset();
              setUseRecovery(v => !v);
            }}>
            {useRecovery
              ? "Use my authenticator app"
              : "I lost my phone: use a recovery code"}
          </button>
        </>
      )}
      {start.data && (
        <>
          <h3 className="text-base font-semibold text-gray-900">
            Scan this QR code with the new authenticator app
          </h3>
          <AuthenticatorQr setup={start.data} />
          <p className="text-sm text-gray-600">
            Enter the code the new app shows.
            {needsStepUp ? " Your old app keeps working until you do." : ""}
          </p>
          <SixDigitCodeInput
            resetKey={attempt}
            disabled={confirm.isPending || lockedUntil !== undefined}
            onComplete={code =>
              confirm.mutate(
                { code },
                { onSuccess: r => onDone(r.recoveryCodes), onError: bump }
              )
            }
          />
        </>
      )}
      {lockedUntil ? (
        <LockedNotice until={lockedUntil} />
      ) : (
        error && (
          <p role="alert" className="text-sm font-semibold text-rose-600">
            {error.message}
          </p>
        )
      )}
      <button
        type="button"
        className="block text-sm font-semibold text-gray-700 hover:text-gray-900"
        onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}
